<template>
  <SlotPanel>
    <div class="font-label text-fg-secondary text-xs uppercase">
      {{ label }}
    </div>
    <div v-if="!amount">Reader has no requested charge</div>
    <div v-else>
      <div>
        Requesting charge: <span class="font-bold">${{ (amount / 100).toFixed(2) }}</span>
      </div>
      <div>
        <ButtonDefault @click="onSimulateSuccess" label="Success" class="mt-3 w-full h-10 py-1.5" />
      </div>
      <div class="flex">
        <ListBox
          :selected="declineCodeSelected"
          :options="declineCodes"
          @listBoxChange="declineCode = $event.value"
          itemText="label"
          itemValue="value"
          label="Decline"
          class="mr-3 mt-3 w-7/12"
          alt
        />
        <ButtonDefault @click="onSimulateDecline" label="Decline" class="mt-3 flex-grow" alt />
      </div>
    </div>
    <div v-if="lastMessage">
      <div>
        Failure Code: <span class="font-bold">{{ lastMessage.failure_code }}</span>
      </div>
      <div>
        Failure Message: <span class="font-bold">{{ lastMessage.failure_message }}</span>
      </div>
    </div>
  </SlotPanel>
</template>
<script lang="ts">
import { useEventBus } from '@vueuse/core'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import { SimulatedEvent, readerKey, simulatedKey } from '@/api/events'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ListBox from '@/common/components/molecules/Listbox.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { DeclineCode, useTerminalStore } from '@/stores/terminal'
import { snakeToCapitalWords } from '@/utils/utils'

import { StripeReaderMessageRead } from '@generated/types'

export default defineComponent({
  name: 'SimulatedReader',
  components: {
    SlotPanel,
    ButtonDefault,
    ListBox,
  },
  data() {
    return {
      amount: 0,
      declineCode: DeclineCode.genericDecline,
      lastMessage: null as StripeReaderMessageRead | null,
    }
  },
  mounted() {
    useEventBus(simulatedKey).on(this.onSimulatedEvent)
    useEventBus(readerKey).on(this.onReaderEvent)
  },
  unmounted() {
    useEventBus(simulatedKey).off(this.onSimulatedEvent)
    useEventBus(readerKey).off(this.onReaderEvent)
  },
  computed: {
    ...mapStores(useTerminalStore),
    declineCodes(): { label: string; value: string }[] {
      return Object.values(DeclineCode).map(code => ({
        label: snakeToCapitalWords(code),
        value: code,
      }))
    },
    declineCodeSelected(): { label: string; value: string } {
      return this.declineCodes.find(code => code.value === this.declineCode) || this.declineCodes[0]
    },
    label(): string {
      return this.terminalStore.selectedReader?.label || 'Unknown'
    },
  },
  methods: {
    onSimulatedEvent(event: SimulatedEvent) {
      if (event.reader_id === this.terminalStore.selectedReader?.id) {
        this.amount = event.amount
      }
    },
    onReaderEvent(event: StripeReaderMessageRead) {
      if (event.reader_id === this.terminalStore.selectedReader?.id) {
        if (event.status == 'succeeded') {
          this.amount = 0
          this.lastMessage = null
        } else {
          this.lastMessage = event
        }
      }
    },
    onSimulateSuccess() {
      this.terminalStore.simulateSuccess()
    },
    onSimulateDecline() {
      this.terminalStore.simulateDecline(this.declineCode)
    },
  },
})
</script>
